import { useMemo, useState } from 'react'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { updateMedia } from '@/infrastructure/storage'
import { createLogger } from '@/shared/logging/logger'
import { clipFileUrl, clipThumbUrl, type CatalogClip } from './api'
import { makeFilename } from './filename'
import { setIdMapEntry } from './idmap'
import { useCatalog } from './use-catalog'

const log = createLogger('smm-catalog')

/** Catalog browser inside the media library. Search + one-click vocab chips. "Add"
 *  imports the clip into this draft's OPFS so it appears in the normal media grid
 *  (drag onto the timeline as usual). When `onPick` is provided (e.g. the Replace
 *  flow), clicking a clip calls it instead of the default Add. */
export function CatalogPanel({
  projectId,
  onImported,
  onPick,
}: {
  projectId: string
  onImported?: (mediaId: string) => void
  onPick?: (catalogId: string) => void
}) {
  const [q, setQ] = useState('')
  const [keyword, setKeyword] = useState<string | undefined>(undefined)
  const filter = useMemo(() => ({ q: q || undefined, keyword }), [q, keyword])
  const { clips, loading, error } = useCatalog(filter)
  const [adding, setAdding] = useState<string | null>(null)

  const chips = useMemo(() => [...new Set(clips.flatMap((c) => c.keywords))].sort(), [clips])

  async function activate(c: CatalogClip) {
    if (onPick) {
      onPick(c.id)
      return
    }
    setAdding(c.id)
    try {
      const meta = await mediaLibraryService.importMediaFromUrl(clipFileUrl(c.id), projectId)
      setIdMapEntry(projectId, c.id, meta.id) // round-trip safety for autosave
      await updateMedia(meta.id, {
        tags: c.keywords,
        description: c.description ?? undefined,
        fileName: makeFilename({ id: c.id, category: c.category }),
      }).catch((e) => {
        // enrichment failure must not block the import path; see spec §6
        log.warn('enrichment skipped for', c.id, e)
      })
      onImported?.(meta.id)
    } finally {
      setAdding(null)
    }
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search catalog…"
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap gap-1">
        <Chip active={!keyword} onClick={() => setKeyword(undefined)}>
          All
        </Chip>
        {chips.map((k) => (
          <Chip key={k} active={keyword === k} onClick={() => setKeyword(k)}>
            {k}
          </Chip>
        ))}
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 overflow-y-auto">
          {clips.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => activate(c)}
              disabled={adding === c.id}
              title={c.description ?? c.keywords.join(', ')}
              className="group relative aspect-[9/16] overflow-hidden rounded-md border border-border disabled:opacity-60"
            >
              {c.hasThumbnail ? (
                <img src={clipThumbUrl(c.id)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  {c.category ?? 'clip'}
                </div>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[10px] text-white">
                {adding === c.id ? 'Adding…' : `${c.durationSec.toFixed(1)}s`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-xs ${
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground'
      }`}
    >
      {children}
    </button>
  )
}
